/**
 * PersonelKartParcalari.jsx — personel kartının PAYLAŞILAN parçaları.
 *
 * Backlog #64 (2026-09-19). Bu dosyadan önce aynı kart ÜÇ KERE yazılmıştı:
 *   · pages/Firma/FirmaPersonelListPage.jsx    (firma tarafı kadro — StatusTile/VDivider/
 *     PersonelDocTiles/PersonelKartRibbon/PersonelSayacStack/PersonelAvatar, hepsi dosya-yerel; PersonelKartRibbon 2026-10-21'de silindi, yerini
 *     Özel İzin şeridi + kart kararı rengi çerçeve/hap aldı)
 *   · pages/Firma/FirmaPersonelDetailPage.jsx  (aynı karelerin bir kopyası daha)
 *   · Components/Kisi/PersonelKart.jsx         (denetmen tarafı Personeller listesi)
 * #64 denetmen tarafındaki Firma Detay > Personel Listesi sekmesine DÖRDÜNCÜSÜNÜ istiyordu;
 * görev tanımının kendi cümlesiyle "kopyalamak yerine ortak bileşene çıkarın".
 *
 * Renk/kare/ayraç PRİMİTİFLERİ burada DEĞİL — Components/Projects/StandPills.jsx'te
 * (StatusTile, VDivider, STATUS_HEX, NEUTRAL_GRAY). Bu dosya yalnızca personele ÖZGÜ
 * katmanı taşır: hangi belge hangi ikon, ribbon neyi gösterir, sayaç yığını, vesikalık
 * kutusu ve "ilişki sonlandırıldı" rozeti.
 *
 * ⚠️ İKİ AYRI SATIR ŞEKLİ VAR, karıştırmayın (rehberin uyarısı: "Do not share a component
 * between them without checking the keys"):
 *   · KADRO satırı (`person_firma_to_fuar`) → documents{}/statusCounts/kartDurumu/muaf
 *   · LİSTE satırı (`person_to_firma`)      → rosterId/durum/fuaraAtanabilir
 * Buradaki bileşenler YALNIZCA kadro satırıyla çalışır; tek istisna `IliskiBitisRozeti`,
 * çünkü dayandığı `iliskiBitisMeta` (personelRosterService.js) ikisini de normalize eder.
 */
import React, { useRef, useState } from "react";
import { Badge, Button, Modal, ModalHeader, ModalBody, Spinner, UncontrolledTooltip, UncontrolledPopover, PopoverBody } from "reactstrap";
import {
  User, ShieldCheck, Landmark, HardHat, HeartPulse, GraduationCap, Building2,
  UserMinus, Info, Shirt, IdCard,
} from "lucide-react";
import DurumSayaclari, { SayacKutusu } from "../Common/DurumSayaclari";
import { StatusTile, VDivider, STATUS_HEX, STATUS_TILE_SIZE, NEUTRAL_GRAY, statusBackground } from "../Projects/StandPills";
import { FirmaGrupKareleri, firmaAdi } from "./PersonelFirmaBelgeParcalari";
import { FILE_APPROVAL_STATUS } from "../../services/projectService";
import { hizmetDokumuNotu } from "../../services/personelBelgeService";
import { KART_KARARI_UC, kartKarariUc, firmaKatilimMeta, portalKartEtiketi } from "../../services/kartDurumuService";
import { iliskiBitisMeta, ESLESME_DURUM_IDS } from "../../services/personelRosterService";
import { EslesmeDurumRozeti } from "./PersonelDurumParcalari";
import useKisiFoto from "./useKisiFoto";
import { meslekiYuvalar } from "./meslekler";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

/** Kişinin kendi dört belgesi — KVKK ayrı, çünkü kareler arasında ayraçla ondan ayrılıyor. */
export const PERSONEL_BELGE_META = [
  { key: "sgk", label: "SGK", icon: Landmark },
  { key: "isg", get label() { return i18n.t("components:personelKart.isg"); }, icon: HardHat },
  { key: "saglik", get label() { return i18n.t("components:personelKart.saglik"); }, icon: HeartPulse },
  { key: "meslekiEgitim", get label() { return i18n.t("components:personelKart.meslekiEgitim"); }, icon: GraduationCap },
];

/** Bir belge durumunun kare rengi + başlığı. `status` null → "Kayıt yok" (nötr gri). */
function belgeKareBilgisi(status, label, neden = null) {
  const info = status != null ? FILE_APPROVAL_STATUS[status] : null;
  return {
    background: info ? STATUS_HEX[info.color] ?? NEUTRAL_GRAY : NEUTRAL_GRAY,
    // #737: a rejected card's tooltip also says why ("SGK — Reddedildi · Red nedeni: Okunmuyor"). WAS: no reason.
    label: `${label} — ${info ? info.label : i18n.t("components:personelKart.kayitYok")}${neden ? ` · ${neden}` : ""}`,
  };
}

/** #737: "Red nedeni: …" for a rejected person document (`documents[key]`, `slot.belge`); null when it is not rejected or gave no reason. */
function redNedeniYazisi(doc) {
  const neden = typeof doc?.rejectReason === "string" ? doc.rejectReason.trim() : "";
  return doc?.status === 2 && neden ? `${i18n.t("portal:firmaKart.redNedeni")} ${neden}` : null;
}

/** #466: Mesleki Eğitim belgesi eksik / reddedilmiş / süresi dolmuş mu — bunların hiçbiri kartı engellemez, kare nötr gri okunur. */
const mesleklikGri = (doc) => doc?.status == null || doc.status === 2 || doc.expired === true;

/** KKD durumu → kare rengi (STATUS_HEX; bu temada rozet sınıfları güvenilmez). */
const KKD_RENK = { UYGUN: STATUS_HEX.success, UYARI: STATUS_HEX.warning, EKSIK: STATUS_HEX.danger };

/**
 * Satırın `kkd` nesnesinden KKD karesinin `{ background, opacity, label }`ı (#374). `kkd` null → düz gri "KKD";
 * ISTENMIYOR → soluk gri + "bu fuarda istenmiyor"; diğerlerinde başlık fuarın istediği maddeleri sayar
 * ("KKD — Yelek ✓, Baret ✗"; ✓ = kişide var). Madde listesi boşsa yalnız "KKD".
 */
function kkdKareBilgisi(kkd, t) {
  if (!kkd) return { background: NEUTRAL_GRAY, opacity: 1, label: t("personelKart.kkd2") };
  if (kkd.durum === "ISTENMIYOR" || kkd.istenen === false) {
    return { background: NEUTRAL_GRAY, opacity: 0.35, label: t("personelKart.kkdIstenmiyor") };
  }
  const maddeler = (kkd.kalemler || []).map((k) => `${k.ad} ${k.mevcut ? "✓" : "✗"}`).join(", ");
  return {
    background: KKD_RENK[kkd.durum] ?? NEUTRAL_GRAY,
    opacity: 1,
    label: maddeler ? t("personelKart.kkd", { v: maddeler }) : t("personelKart.kkd2"),
  };
}

/**
 * Bir mesleğin sertifika yuvasının `{ background, opacity, durum }`u (#372). Renk `belge.status`ten (belge yoksa nötr gri);
 * istenen bir meslekte onaylı görünen ama SAYILMAYAN sertifika (süresi dolmuş, fuar için eski, etiketi başka) sarı okunur —
 * yeşil "tamam" demek olurdu. İstenmeyen meslek soluk basılır ve yargılanmaz.
 */
function meslekYuvaBilgisi(yuva, t) {
  const status = yuva.belge?.status ?? null;
  const info = status != null ? FILE_APPROVAL_STATUS[status] : null;
  const sayilmiyor = yuva.istenen && status === 1 && yuva.sayilir === false;
  // WAS (until #466): const background = sayilmiyor ? STATUS_HEX.warning : (info ? STATUS_HEX[info.color] ?? NEUTRAL_GRAY : NEUTRAL_GRAY);
  // #466: Mesleki Eğitim kartı ENGELLEMEZ (kart "Çalışan" olarak basılır); kırmızı "kart çıkmaz" demektir, bu yüzden reddedilmiş ya da
  // süresi dolmuş sertifika da eksik gibi nötr gri okunur. Bekleyen sarı kalır (personelin işi var), onaylı yeşil.
  const background = sayilmiyor ? STATUS_HEX.warning : (mesleklikGri(yuva.belge) ? NEUTRAL_GRAY : (info ? STATUS_HEX[info.color] ?? NEUTRAL_GRAY : NEUTRAL_GRAY));
  const durum = [
    info ? info.label : t("personelKart.kayitYok"),
    sayilmiyor ? t("personelKart.sayilmiyor") : null,
    yuva.istenen ? null : t("personelKart.meslekIstenmiyor"),
    redNedeniYazisi(yuva.belge), // #737
  ].filter(Boolean).join(" · ");
  return { background, opacity: yuva.istenen ? 1 : 0.35, durum };
}

/**
 * Mesleki Eğitim karesinin yanındaki "+N" (N = meslek sayısı - 1): kişi bu fuarda 2+ meslek taşıyorsa her mesleğin kendi
 * sertifika karesini aşağı doğru açılan popover'da gösterir — `DigerFirmalarRozeti`nin meslek karşılığı (#372). Kareler
 * bağlantı verilirse kişinin Meslek sayfasını yeni sekmede açar. Hiçbir mesleğe sayılmayan etiketsiz belge varsa altında söylenir.
 */
export function MeslekiEgitimlerRozeti({ personId, yuvalar, etiketsiz = 0, href }) {
  const { t } = useTranslation("components");
  if (!yuvalar || yuvalar.length < 2) return null;
  const id = `meslek-diger-${personId}`;
  return (
    <>
      <Button
        id={id} type="button" color="light" size="sm" className="border text-nowrap"
        title={t("personelKart.digerMeslek", { length: yuvalar.length - 1 })}
      >
        +{yuvalar.length - 1}
      </Button>
      <UncontrolledPopover target={id} trigger="legacy" placement="bottom">
        <PopoverBody>
          <div className="vstack gap-2">
            {yuvalar.map((y) => {
              const b = meslekYuvaBilgisi(y, t);
              return (
                <div key={y.meslekId} className="d-flex align-items-center gap-2">
                  <div className="flex-grow-1 small text-truncate" style={{ minWidth: 120, maxWidth: 220 }} title={y.meslekAdi}>
                    {y.meslekAdi}
                  </div>
                  <StatusTile
                    icon={GraduationCap} background={b.background} opacity={b.opacity}
                    label={`${y.meslekAdi} — ${b.durum}`} href={href}
                  />
                </div>
              );
            })}
            {etiketsiz > 0 && (
              <div className="small text-warning">{t("personelKart.etiketsizBelge", { length: etiketsiz })}</div>
            )}
          </div>
        </PopoverBody>
      </UncontrolledPopover>
    </>
  );
}

/**
 * Belge kareleri: KVKK | SGK · İSG · Sağlık | Mesleki Eğitim (+N) | Firma İSG Belgeleri.
 *
 * @param documents            kadro satırının `documents{}` nesnesi
 * @param gerekliBelgeler      mesleğin istediği anahtarlar; boş/verilmemişse hepsi istenir
 *                             sayılır. İstenmeyen kare soluk (opacity .35) basılır —
 *                             denominatör 2026-08-28'de sabit dörtlü olmaktan çıktı.
 * @param firmaBelgeleriStatus üç İSG belgesinin toplamı; `undefined` ise kare hiç basılmaz
 *                             (firma bağlamı olmayan kişi dizini için)
 * @param firmaBelgeGruplari   bir firmanın İKİ grubu (`belgeGruplari[]` / `firmaBelgeGruplari[]`,
 *                             backend #123): Firma Belgeleri + Firma İSG Belgeleri ayrı iki kare.
 *                             Verilirse `firmaBelgeleriStatus` karesinin yerine geçer (o kare
 *                             aslında yalnızca İSG grubuydu); boş dizi/null → firma karesi basılmaz
 * @param firmaEki             firma karelerinin yanına konan düğüm ("+N" diğer firmalar)
 * @param onDocClick           (docKey) => void; verilmezse kareler salt görsel
 * @param hrefOf               (docKey) => adres | undefined; adres dönen kare YENİ SEKMEDE açılan bağlantı olur
 *                             (#273). `onDocClick`ten bağımsız: ikisi de verilirse href kazanır.
 * @param firmaHref            firma karelerinin (Firma Belgeleri / Firma İSG Belgeleri) yeni sekme adresi
 * @param kkd                  satırın `kkd` nesnesi (backend #373, #374): verilirse (null dahil) firma karelerinden sonra silik bir
 *                             çizgiyle ayrılmış TEK KKD karesi basılır, rengi `kkd.durum`dan — UYGUN yeşil · UYARI sarı · EKSIK kırmızı ·
 *                             ISTENMIYOR soluk gri; başlıkta fuarın istediği maddeler ("Yelek ✓, Baret ✗"). `null` (üst çubukta fuar yok) →
 *                             düz gri kare. `undefined` (eski yanıt) → aşağıdaki `yelek`e düşülür. Yalnızca Kişi Listesi satırı verir —
 *                             bu bileşeni dört sayfa paylaşıyor, onların verisi yok.
 * @param yelek                ESKİ yol — yalnızca `kkd` hiç gelmediğinde: `{ verildi: boolean }` → yeşil/gri KKD karesi (`givenVest`, #285).
 * @param kvkkDijitalOnay     satırın `kvkkDijitalOnay` nesnesi (backend #139); `tamam` ise KVKK karesi
 *                             belge olmasa da YEŞİL — giriş kuralı üç kaynaktan birini kabul ediyor
 *                             (onaylı belge · durum 3 · dijital onay). Verilmezse yalnız belgeye bakılır.
 * @param ekBelgeler           [{ key, label, icon }] — dörtlünün dışındaki istenen/yüklenmiş belgeler
 *                             (yüksekte çalışma, aşı kartı, kimlik fotoğrafı …); kare olarak dörtlüden sonra gelir
 * @param meslekiEgitimler     satırın `meslekiEgitimler[]`i (backend #371, #372): 2+ meslek varsa Mesleki Eğitim karesi (istenenlerin
 *                             en kötüsü, `documents.meslekiEgitim`) "+N" düğmesi ve her mesleğin kendi karesiyle birlikte basılır; karenin
 *                             ipucu da mesleklerin durumunu sayar. `null`/tek meslek → eskisi gibi tek kare. SGK · İSG · Sağlık ile Mesleki
 *                             Eğitim arasındaki silik ayraç HER ZAMAN basılır (fuarsız satır dahil).
 * @param etiketsizBelgeler    satırın `etiketsizMeslekiEgitimBelgeleri[]` sayısı — popover'da uyarı olarak
 * @param personId             "+N" popover'ının hedef kimliği için (sayfada bir kişi birden çok satırda olmaz)
 * @param bolum                #531: "kvkk" | "kisisel" (SGK · İSG · Sağlık) | "mesleki" (Mesleki Eğitim + "+N") | "firma" (firma grupları + "+N")
 *                             | "diger" (KKD). Verilirse YALNIZCA o bölümün kareleri basılır ve ayraç çizilmez: Kişi Listesi kartı
 *                             bölümleri başlıklarıyla ayrı ayrı yerleştirir (firma adı kişisel evrakların önüne girer). Verilmezse
 *                             eski tek satır — firma portalı ve Firma Detay listeleri böyle kullanır.
 * @param etiketli             #531: her karenin altına "SGK" / "İSG" / "Meslek" gibi küçük bir yazı konur (yalnızca `bolum` ile anlamlı).
 */
export function PersonelBelgeKareleri({
  documents, gerekliBelgeler, firmaBelgeleriStatus, firmaBelgeGruplari, firmaEki, onDocClick,
  kvkkDijitalOnay, ekBelgeler, hrefOf, firmaHref, kkd, yelek, meslekiEgitimler, etiketsizBelgeler = 0, personId,
  bolum, etiketli = false,
}) {
  const { t } = useTranslation("components");
  const goster = (b) => !bolum || bolum === b;
  const ayrac = !bolum;
  const yazi = (metin) => (etiketli ? metin : undefined);
  // Bölümlü düzende kare + yazı yığını karenin yanındaki "+N" düğmesini aşağı iter: düğme karenin yüksekliğinde ortalanır.
  const kareYaninda = (dugum) => (bolum && dugum
    ? <div className="d-flex align-items-center" style={{ height: STATUS_TILE_SIZE }}>{dugum}</div>
    : dugum);
  const docs = documents || {};
  const gerekli = new Set(gerekliBelgeler || []);
  const tumuGerekli = gerekli.size === 0;
  const tikla = (key) => (onDocClick ? () => onDocClick(key) : undefined);
  const bagla = (key) => hrefOf?.(key) || undefined;

  const kvkk = kvkkDijitalOnay?.tamam && docs.kvkk?.status !== 1
    ? { background: STATUS_HEX.success, label: t("personelKart.kvkkDijitalOnay") }
    : belgeKareBilgisi(docs.kvkk?.status, "KVKK", redNedeniYazisi(docs.kvkk));
  // #140 — üçlü (79/88/89) İSG grubudur, ekranda "Firma İSG Belgeleri"; payload anahtarı
  // (firmaBelgeleriStatus) sabit kalır.
  const firma = belgeKareBilgisi(firmaBelgeleriStatus, t("personelKart.firmaIsgBelgeleri"));
  // #374: KKD karesi. `kkd` hiç gelmediyse (undefined) eski Yelek yolu; yoksa tek kare, renk `kkd.durum`dan.
  const kkdKare = kkd !== undefined ? kkdKareBilgisi(kkd, t) : yelek ? {
    background: yelek.verildi ? STATUS_HEX.success : NEUTRAL_GRAY,
    label: t("personelKart.kkd", { v: yelek.verildi ? t("personelKart.verildi") : t("personelKart.verilmedi") }),
  } : null;

  return (
    <div className={bolum ? "d-flex align-items-start gap-2" : "d-flex align-items-center flex-wrap gap-2"}>
      {goster("kvkk") && (
        <StatusTile
          icon={ShieldCheck} background={kvkk.background} label={kvkk.label} caption={yazi("KVKK")}
          onClick={tikla("kvkk")} href={bagla("kvkk")} tour="personel-kvkk"
        />
      )}
      {ayrac && <VDivider className="d-none d-md-block" />}
      {PERSONEL_BELGE_META.filter((d) => !bolum || (d.key === "meslekiEgitim" ? bolum === "mesleki" : bolum === "kisisel")).map((d) => {
        const mesleki = d.key === "meslekiEgitim";
        // #372: tek meslek ya da fuarsız satırda `yuvalar` null → eski tek kare; 2+ meslekte ipucu her mesleğin durumunu sayar.
        const yuvalar = mesleki ? meslekiYuvalar({ meslekiEgitimler }) : null;
        const cokMeslek = yuvalar && yuvalar.length > 1;
        // #466: `gerekliBelgeler` artık "Çalışan" seti — Mesleki Eğitim hiçbirinde yok; kişinin tuttuğu meslekten biri istiyorsa kare tam
        // renkte basılır (istemeyen soluk). WAS (until #466): const istenen = tumuGerekli || gerekli.has(d.key);
        const istenen = mesleki && yuvalar ? yuvalar.some((y) => y.istenen) : (tumuGerekli || gerekli.has(d.key));
        const kare = belgeKareBilgisi(docs[d.key]?.status, d.label, redNedeniYazisi(docs[d.key]));
        // #268: onaylı ama eski (ya da ayı girilmemiş) SGK Hizmet Dökümü — kare yeşil kalır (kartı engellemez, #267), köşede sarı nokta ve
        // ipucunda "Ağustos 2026 — 3 ay eski" notu; kartı basan kişi e-Devlet'e bakıp bakmayacağına kendisi karar verir.
        const hizmetNotu = d.key === "sgk" ? hizmetDokumuNotu(docs.sgk) : null;
        if (hizmetNotu) kare.label = `${kare.label} · ${hizmetNotu}`;
        // #466: kare gri okunur ve ipucu kartın ne olacağını söyler (engel değil, bilgi).
        if (mesleki && istenen && mesleklikGri(docs[d.key])) {
          kare.background = NEUTRAL_GRAY;
          kare.label = `${kare.label} · ${t("personelKart.calisanOlarakBasilir")}`;
        }
        const ipucu = cokMeslek
          ? [kare.label, ...yuvalar.map((y) => `• ${y.meslekAdi}: ${meslekYuvaBilgisi(y, t).durum}`)].join("\n")
          : kare.label;
        return (
          <React.Fragment key={d.key}>
            {/* #372: kişinin SGK · İSG · Sağlık'ı ile mesleğe bağlı sertifika arasında silik ayraç. */}
            {mesleki && ayrac && <VDivider className="d-none d-md-block" />}
            <StatusTile
              icon={d.icon}
              background={kare.background}
              opacity={istenen ? 1 : 0.35}
              label={istenen ? ipucu : t("personelKart.buMeslekIcinIstenmiyor", { label: ipucu })}
              marker={hizmetNotu ? STATUS_HEX.warning : undefined}
              caption={yazi(mesleki ? t("personelKart.meslek") : d.label)}
              onClick={tikla(d.key)}
              href={bagla(d.key)}
            />
            {cokMeslek && kareYaninda(
              <MeslekiEgitimlerRozeti
                personId={personId} yuvalar={yuvalar} etiketsiz={etiketsizBelgeler} href={bagla(d.key)}
              />,
            )}
          </React.Fragment>
        );
      })}
      {goster("kisisel") && (ekBelgeler || []).map((d) => {
        const istenen = gerekli.has(d.key);
        const kare = belgeKareBilgisi(docs[d.key]?.status, d.label, redNedeniYazisi(docs[d.key]));
        return (
          <StatusTile
            key={d.key}
            icon={d.icon}
            background={kare.background}
            opacity={istenen ? 1 : 0.35}
            label={istenen ? kare.label : t("personelKart.buMeslekIcinIstenmiyor", { label: kare.label })}
            caption={yazi(d.label)}
            onClick={tikla(d.key)}
            href={bagla(d.key)}
          />
        );
      })}
      {goster("firma") && (firmaBelgeGruplari !== undefined ? (
        firmaBelgeGruplari?.length > 0 && (
          <>
            {ayrac && <VDivider className="d-none d-md-block" />}
            <FirmaGrupKareleri gruplar={firmaBelgeGruplari} href={firmaHref} etiketli={etiketli} />
            {kareYaninda(firmaEki)}
          </>
        )
      ) : firmaBelgeleriStatus !== undefined && (
        <>
          {ayrac && <VDivider className="d-none d-md-block" />}
          <StatusTile
            icon={Building2}
            background={firma.background}
            label={firma.label}
            caption={yazi(t("personelKart.etiketFirma"))}
            onClick={tikla("firmaBelgeleri")}
            href={bagla("firmaBelgeleri")}
          />
        </>
      ))}
      {goster("diger") && kkdKare && (
        <>
          {ayrac && <VDivider className="d-none d-md-block" />}
          {/* #481: KKD karesi de yeni sekmede açılan bağlantı — kişi sayfasının Kontroller sekmesinde KKD kartını çerçeveler. */}
          <StatusTile
            icon={Shirt} background={kkdKare.background} opacity={kkdKare.opacity} label={kkdKare.label}
            caption={yazi(t("personelKart.kkd2"))} href={bagla("kkd")} tour="personel-kkd"
          />
        </>
      )}
    </div>
  );
}

/**
 * #531: sol üst şerit YALNIZCA verilmiş Özel İzin için. Rengi sabit kırmızı (çizimdeki gibi; durumla değişmez). Hex, çünkü bu temada
 * `bg-danger` sınıfına güvenilmez. İpucu: izin tipi · veren · tarih. Kartın kendisi `position-relative overflow-hidden` olmalı.
 * (Kişi Listesi'nden buraya taşındı: Yabancı Katılımcı kartı da aynısını çiziyor.)
 */
export function OzelIzinRibbon({ izin }) {
  const { t } = useTranslation("components");
  const tarih = izin?.givenAt ? new Date(String(izin.givenAt).replace(" ", "T")) : null;
  const baslik = [
    t("personelKart.ozelIzinIpucu"),
    izin?.typeAdi || izin?.label || izin?.typeString,
    izin?.givenBy,
    tarih && !Number.isNaN(tarih.getTime()) ? tarih.toLocaleDateString(currentLocaleTag()) : null,
  ].filter(Boolean).join(" · ");
  return (
    <span
      className="badge position-absolute text-white"
      title={baslik}
      style={{
        top: 12, left: -6, padding: "4px 22px 4px 10px", background: STATUS_HEX.danger,
        clipPath: "polygon(0 0, 100% 0, 88% 50%, 100% 100%, 0 100%)", fontSize: "0.7rem", zIndex: 2,
      }}
    >
      {t("personelKart.ozelIzin")}
    </span>
  );
}

/**
 * Firmadaki rol yazısı (#370): İşveren ya da Çalışan; `firma.isveren` boolean değilse (eski yanıt) hiçbir şey.
 * #531: rengi kişinin o firmadaki durumu (`firmaKatilimMeta`), iki rol de aynı renkleri alır. Kişi Listesi'nden buraya taşındı:
 * Yabancı Katılımcı kartı `firmaKatilimMeta`nın okuduğu alanları satırın `fuarDurumu`ndan kurar.
 */
export function RolRozeti({ firma }) {
  const { t } = useTranslation("components");
  if (typeof firma?.isveren !== "boolean") return null;
  const rol = firma.isveren ? t("personelKart.isveren") : t("personelKart.calisan");
  const katilim = firmaKatilimMeta(firma);
  return (
    <span
      className="badge text-white fw-normal" style={{ background: statusBackground(katilim.color) }}
      title={t("personelKart.firmadaRolDurumu", { firmaAdi: firmaAdi(firma), rol, durum: katilim.label })}
    >
      {rol}
    </span>
  );
}

const KART_HAPI_GENISLIK = 140;

/**
 * "Kart Durumu" hapı (Deniz'in Yabancı Katılımcı çizimi): vesikalığın altında, çerçeveyle AYNI renkte çerçeveli yazı = üç durumlu kart
 * kararı (`kartKarariUc`). Kişi Listesi'nde aynı yerde "Kart Yazdır" düğmesi durur; firma tarafı kart basmaz, hap yalnızca kararı söyler.
 * Karar hesaplanmadıysa (kişi bu fuarda görevli değil, ayrıntısı okunmadı) gri "Kart Durumu" yazar.
 */
export function KartKarariHapi({ karar, className = "" }) {
  const { t } = useTranslation("components");
  const hex = statusBackground(karar.color);
  const baslik = [
    karar.label,
    karar.key === "ALANDA_KART_ALABILIR" ? t("personelKart.kararAlanda") : null,
    karar.key === "KART_ALAMAZ" ? t("personelKart.kararKartAlamaz") : null,
  ].filter(Boolean).join(" · ");
  return (
    <span
      className={`d-inline-block text-center text-nowrap rounded-pill fw-semibold ${className}`} title={baslik} data-tour="personel-yetki"
      // #620: one fixed width, one line: "Partially Authorized" used to break (WAS: maxWidth 124) and the width of the photo column then differed per row.
      style={{ border: `2px solid ${hex}`, color: hex, fontSize: "0.7rem", lineHeight: 1.15, padding: "2px 9px", width: KART_HAPI_GENISLIK }}
    >
      {karar.hesaplandi ? karar.label : t("personelKart.kartDurumu")}
    </span>
  );
}

/**
 * Vesikalık + "Kart Durumu" hapı, alt alta: firma portalı listelerinin (FirmaPersonelSekmeleri) satır başı. Çerçeve ile hap AYNI renk = üç
 * durumlu kart kararı, `dto`dan (kişinin fuar KADROSU ayrıntısı, `GET /personnel/firma/{firmaId}/{personId}?fuarId=`) okunur; kadro dışı satırda
 * `dto` yoktur → karar hesaplanmaz, gri. Fotoğraf satırın `fotografVar` bayrağıyla okunur (`useKisiFoto`; okunamazsa kişi ikonu).
 * `serit`: kartta Özel İzin şeridi var → çerçevenin rengini örtmesin diye aşağı itilir.
 * `portal` (#539): firma portalı — hap "Yetkili / Kısmen Yetkili / Yetkisiz" der (Authorized / Partially Authorized / Unauthorized); denetmen
 * listeleri (Firma Detay) kart adlarını korur.
 * WAS (until 2026-10-21): bu satırlarda siyah çerçeveli boş bir kutu ve köşede üç durumlu "Kart Alabilir" şeridi (PersonelKartRibbon) vardı.
 *
 * `onFoto` (backlog #588; firma portalı): verilirse vesikalık kutusuna tıklamak dosya seçtirir ve `onFoto(file)` çağrılır (kutunun
 * üstünde yükleme sürerken bir döner gösterge çıkar; WAS until #620: köşede kamera rozeti). `onFoto` bir Promise döner, true ile bitince
 * fotoğraf hemen yeniden okunur. Kimliği SAKA'ca doğrulanmış kişide çağıran `onFoto` VERMEZ (#726): vesikalık düz bir fotoğraftır —
 * ne işaretçi, ne yükleme ipucu, ne de kilit yazısı (backend firmaya 403 KIMLIK_DOGRULANMIS verir; arada doğrulanırsa mesajı çağıran gösterir).
 * Denetmen listeleri `onFoto` vermez (staff kendi Kişi Listesi / Kişi Detay yollarıyla yükler).
 * WAS (until #726): `fotoKilitli` prop'u kutunun başlığına "Kimliği SAKA tarafından doğrulandı; fotoğrafı yalnızca SAKA değiştirebilir" eklerdi.
 */
export function KartKarariVesikalik({ person, dto, serit = false, portal = false, onFoto, fotoYukleniyor = false }) {
  const { t } = useTranslation("components");
  const girdiRef = useRef(null);
  const [surum, setSurum] = useState(0);
  const foto = useKisiFoto(person.personId, person.fotografVar, `${person.logo ?? ""}:${surum}`);
  const karar = kartKarariUc(dto, { ozelIzin: dto?.singlePermit || null, kkd: dto?.kkd, portal });

  async function dosyaSecildi(e) {
    const dosya = e.target.files?.[0];
    e.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (dosya && (await onFoto(dosya))) setSurum((n) => n + 1);
  }

  return (
    <div className={`d-flex flex-column align-items-center gap-2${serit ? " mt-2" : ""}`}>
      <div className="position-relative flex-shrink-0" data-tour="personel-foto">
        <PersonelAvatar
          photo={foto} cerceve={{ color: karar.color, label: karar.label }}
          onClick={onFoto && !fotoYukleniyor ? () => girdiRef.current?.click() : undefined} aciklama={t("personelKart.fotoYukle")}
        />
        {onFoto && (
          <>
            <input ref={girdiRef} type="file" accept="image/jpeg,image/png,image/webp" className="d-none" onChange={dosyaSecildi} />
            {/* WAS (until #620): a camera chip on the box's bottom-right corner (24 px circle, spinner while uploading). Removed at Deniz's
                request (2026-10-30); the box itself is the upload button, and the spinner now sits over the photo while it uploads. */}
            {fotoYukleniyor && (
              <span
                className="position-absolute top-0 start-0 w-100 h-100 rounded-3 bg-white bg-opacity-75 d-flex align-items-center justify-content-center"
                style={{ pointerEvents: "none" }}
              >
                <Spinner size="sm" color="secondary" />
              </span>
            )}
          </>
        )}
      </div>
      <KartKarariHapi karar={karar} />
    </div>
  );
}

/**
 * Sağda Bekleyen/Onaylanan/Reddedilen belge sayaçları — renkli kareler, sıfırlar çizilmez (DurumSayaclari, #283).
 *
 * `href` verilirse çizilen kareler o adrese YENİ SEKMEDE açılan bağlantı olur (backlog #283): hepsi aynı yere,
 * kişinin Belgeler sayfasına gider — hangi durumun sayıldığına göre süzülmez.
 */
export function PersonelSayacStack({ counts, href, reserve = true }) {
  const { t } = useTranslation("components");
  return (
    <DurumSayaclari
      reserve={reserve}
      items={[
        { key: "pending", tone: "warning", value: counts?.pending, label: t("personelKart.bekleyenBelge"), href },
        { key: "approved", tone: "success", value: counts?.approved, label: t("personelKart.onaylananBelge"), href },
        { key: "rejected", tone: "danger", value: counts?.rejected, label: t("personelKart.reddedilenBelge"), href },
      ]}
    />
  );
}

/**
 * Vesikalık kutusu — fotoğraf yoksa nötr kişi ikonu.
 *
 * `cerceve` ({ color, label }, backlog #126) verilirse çerçeve o renkte basılır (`color` bir rozet rengi adı:
 * success/warning/danger, "bilinmiyor" için secondary → nötr gri; `label` başlık olur). Verilmezse eski siyah
 * çerçeve. Çerçevenin ANLAMI çağırana ait: Kişi Listesi'nde #362'den beri KİMLİK DOĞRULAMA (yeşil doğrulandı ·
 * gri doğrulanmadı · kırmızı reddedildi); kart kararı köşe şeridinde (önceden tersiydi, #126).
 *
 * `onClick` (backlog #131) verilirse kutu klavyeyle de ulaşılabilen bir düğme olur (Tab · Enter ·
 * Boşluk); `aciklama` başlığa eklenir. Yalnızca denetmen listesi verir — aynı kutuyu kullanan firma
 * portalı listesi vermez, orada tıklanacak bir şey yok.
 */
export function PersonelAvatar({ photo, size = 72, cerceve, onClick, aciklama }) {
  const baslik = [cerceve?.label, onClick ? aciklama : null].filter(Boolean).join(" — ") || undefined;
  const tikla = onClick
    ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); }
        },
      }
    : {};
  return (
    <div
      className={`rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden bg-light${cerceve ? "" : " border border-dark"}`}
      style={{
        ...(cerceve
          ? { width: size, height: size, border: `4px solid ${statusBackground(cerceve.color)}` }
          : { width: size, height: size, borderWidth: 2 }),
        ...(onClick ? { cursor: "pointer" } : null),
      }}
      title={baslik}
      {...tikla}
    >
      {photo ? (
        <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <User size={Math.round(size * 0.44)} className="text-muted" />
      )}
    </div>
  );
}

/**
 * Kişinin adı — "Detaylar" düğmesiyle AYNI yere giden bağlantı (backlog #354). Gerçek bir `href` taşır, böylece
 * Ctrl/orta tık/"yeni sekmede aç" tarayıcının kendi işidir; düz sol tık ise sayfanın kendi `onGit`ini (router
 * state'i, kaydırma konumu vb. ile) çağırır. `href`/`onGit` yoksa düz yazı. Düz metin gibi görünür (rengi
 * devralır), üzerine gelince altı çizilir. Metin içermez: firma portalı listesi de kullanır.
 */
export function PersonelAdBaglantisi({ href, onGit, className = "", children }) {
  const [ustunde, setUstunde] = useState(false);
  if (!href || !onGit) return <span className={className}>{children}</span>;
  const tikla = (e) => {
    // Değiştirici tuşlu / sol olmayan tık tarayıcıya kalır (yeni sekme).
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onGit();
  };
  return (
    <a
      href={href} onClick={tikla} className={className}
      onMouseEnter={() => setUstunde(true)} onMouseLeave={() => setUstunde(false)}
      onFocus={() => setUstunde(true)} onBlur={() => setUstunde(false)}
      style={{ color: "inherit", cursor: "pointer", textDecoration: ustunde ? "underline" : "none" }}
    >
      {children}
    </a>
  );
}

/**
 * "İlişki Sonlandırıldı" rozeti — backend #65. Kadro satırında yalnızca fuar FİİLEN
 * sürüyorken gelir (sürmüyorsa satır listeden tamamen düşer), liste satırında `aktif = 0`
 * iken. Satırda iz yoksa hiçbir şey basılmaz.
 */
export function IliskiBitisRozeti({ record, className = "" }) {
  const { t } = useTranslation("components");
  const meta = iliskiBitisMeta(record);
  if (!meta.sonlandirildi) return null;
  const detay = [meta.tarih, meta.bitirenAdi, meta.kaynakAdi].filter(Boolean).join(" · ");
  return (
    <Badge
      color="dark" className={`d-inline-flex align-items-center gap-1 fw-normal ${className}`}
      title={[detay, meta.sebep].filter(Boolean).join(" — ") || undefined}
    >
      <UserMinus size={11} />{t("personelKart.iliskiSonlandirildi")}
    </Badge>
  );
}

// ─── Legend / "(i)" açıklama modalı — backlog #107 ─────────────────────────
// Personel satırlarındaki belge kareleri, köşe rozeti, sayaç yığını ve ilişki-bitiş
// rozetinin ne anlama geldiğini hiçbir yer açıklamıyordu. Legend içeriği bu dosyanın
// KENDİ sabitlerinden (PERSONEL_BELGE_META, FILE_APPROVAL_STATUS, KART_KARARI_UC) üretiliyor,
// ayrı bir metin listesi olarak YAZILMIYOR — böylece bir durum eklenip/değiştirildiğinde
// legend kendiliğinden güncel kalır. Hem denetmen (PersonelIncelemePage) hem firma portalı
// (FirmaPersonelListPage) aynı satır parçalarını paylaştığı için legend de paylaşılıyor.

const BELGE_DURUM_SIRASI = [0, 1, 2]; // FILE_APPROVAL_STATUS anahtarları — Bekliyor/Onaylandı/Reddedildi

// Fuar Kadrosu'ndaki eşleme rozeti (backlog #114) — belge durumuyla AYNI üç sözcük, farklı şey.
const ESLESME_ACIKLAMA = {
  get 0() { return i18n.t("components:personelKart.sakaKisiyiAlandaFirmayaEsledi"); },
  get 1() { return i18n.t("components:personelKart.firmaEslemeyiOnayladiFirmaninKendi"); },
  get 2() { return i18n.t("components:personelKart.firmaEslemeyiReddettiKisiBu"); },
};

function LegendSwatch({ background, children }) {
  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span
        className="rounded-2 flex-shrink-0"
        style={{ width: 18, height: 18, background }}
      />
      {children}
    </span>
  );
}

function LegendSection({ title, children }) {
  return (
    <div className="mb-3">
      <div className="text-uppercase text-muted fw-semibold small mb-2">{title}</div>
      <div className="vstack gap-2">{children}</div>
    </div>
  );
}

/** Lejanttaki şerit örneği — kartın köşe şeridiyle aynı renk/yazı, konumsuz. */
function LegendSerit({ hex, children }) {
  return <span className="badge text-white fw-normal flex-shrink-0" style={{ background: hex }}>{children}</span>;
}

/**
 * Şerit YALNIZCA verilmiş Özel İzin'i söyler, vesikalık çerçevesi (ve düğme / hap) üç durumlu kart kararıdır — her listede (#531, firma listelerine
 * 2026-10-21). WAS (until #531): şerit kart kararını (yeşil üç seviye), çerçeve kimlik doğrulamayı söylüyordu (#362).
 * @param kisiListesi  denetmen Kişi Listesi'nin lejantı: ayrıca Kimlik karesi ve rol rozetinin rengi (firmadaki durum) anlatılır; karar düğmesi
 *                     "Kart Yazdır". Firma portalı / Firma Detay listeleri vermez → karar "Kart Durumu" hapıdır.
 * @param portal       #539: firma portalı listesi — çerçeve örnekleri hapla aynı adı taşır (Yetkili / Kısmen Yetkili / Yetkisiz)
 */
function PersonelLegendModal({ isOpen, toggle, kisiListesi = false, portal = false }) {
  const { t } = useTranslation("components");
  // Kişi Listesi'nde çerçeveyle aynı renkli düğme "Kart Yazdır", firma listelerinde "Kart Durumu" hapı.
  const dugme = kisiListesi ? t("personelKart.kartYazdir") : t("personelKart.kartDurumu");
  const belgeler = [
    ...(kisiListesi ? [{ label: t("personelKart.kimlik"), icon: IdCard, note: t("personelKart.kimlikKaresiAciklama") }] : []),
    { label: "KVKK", icon: ShieldCheck },
    ...PERSONEL_BELGE_META,
    { label: t("personelKart.firmaIsgBelgeleri"), icon: Building2, note: t("personelKart.yalnizcaFirmaBaglamindaGorunur") },
    { label: t("personelKart.kkd2"), icon: Shirt, note: t("personelKart.yalnizcaKisiListesiNdeYesil") },
  ];

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>{t("personelKart.personelKartiIsaretleriNeAnlama")}</ModalHeader>
      <ModalBody>
        <LegendSection title={t("personelKart.belgeKareleri")}>
          <div className="d-flex flex-wrap gap-3 mb-2">
            {belgeler.map((b) => (
              <span key={b.label} className="d-inline-flex align-items-center gap-1 small">
                <b.icon size={14} className="text-muted" />
                {b.label}{b.note && <span className="text-muted"> ({b.note})</span>}
              </span>
            ))}
          </div>
          {BELGE_DURUM_SIRASI.map((status) => {
            const info = FILE_APPROVAL_STATUS[status];
            return (
              <LegendSwatch key={status} background={STATUS_HEX[info.color] ?? NEUTRAL_GRAY}>
                {info.label}
              </LegendSwatch>
            );
          })}
          <LegendSwatch background={NEUTRAL_GRAY}>{t("personelKart.kayitYok")}</LegendSwatch>
          <div className="text-muted small">{t("personelKart.solukDonukBirKareO")}</div>
        </LegendSection>

        <LegendSection title={t("personelKart.koseRozeti")}>
          {/* #531: şerit yalnızca verilmiş Özel İzin için çıkar (kırmızı, sabit); kart kararı çerçevede. 2026-10-21: firma portalı ve Firma Detay
              listeleri de aynı ilkeye geçti (WAS: üç durumlu "Kart Alabilir" şeridi), lejant her yerde aynı. */}
          <span className="d-flex align-items-center gap-2">
            <LegendSerit hex={STATUS_HEX.danger}>{t("personelKart.ozelIzin")}</LegendSerit>
            <span className="text-muted small">{t("personelKart.ribbonOzelIzinAciklama")}</span>
          </span>
        </LegendSection>

        {/* #531: vesikalığın çerçevesi ile Kart Yazdır düğmesi üç durumlu kart kararını söyler (WAS (until #531): çerçeve kimlik doğrulamaydı, #362).
            Firma listelerinde düğmenin yerinde "Kart Durumu" hapı durur: aynı renk, aynı anlam. */}
        <LegendSection title={t("personelKart.cerceveKarari", { dugme })}>
          {[
            [KART_KARARI_UC.KART_ALABILIR, "cerceveKartAlabilir"],
            [KART_KARARI_UC.ALANDA_KART_ALABILIR, "cerceveAlanda"],
            [KART_KARARI_UC.KART_ALAMAZ, "cerceveKartAlamaz"],
          ].map(([meta, anahtar]) => (
            <span key={anahtar} className="d-flex align-items-center gap-2">
              <LegendSwatch background={statusBackground(meta.color)}>{portal ? portalKartEtiketi(meta.color) : meta.label}</LegendSwatch>
              <span className="text-muted small">{t(`personelKart.${anahtar}`, { dugme })}</span>
            </span>
          ))}
          <span className="d-flex align-items-center gap-2">
            <LegendSwatch background={NEUTRAL_GRAY}>{t("services:kartDurumuService.kartDurumuBilinmiyor")}</LegendSwatch>
            <span className="text-muted small">{t(kisiListesi ? "personelKart.cerceveBilinmiyor" : "personelKart.cerceveBilinmiyorFirma", { dugme })}</span>
          </span>
        </LegendSection>

        {kisiListesi && (
          // #531: firma adının altındaki rol yazısının rengi = kişinin o firmadaki durumu (üst çubuktaki fuarda).
          <LegendSection title={t("personelKart.rolRenkleri")}>
            {[
              [{ aktifFuarda: true, aktifFuarEslesmeDurumu: 0 }, "rolEslendiAciklama"],
              [{ aktifFuarda: true, aktifFuarEslesmeDurumu: 1 }, "rolGorevlendirildiAciklama"],
              [{ aktifFuarda: true, aktifFuarEslesmeDurumu: 2 }, "rolReddedildiAciklama"],
              [{ aktifFuarda: false }, "rolYokAciklama"],
            ].map(([firma, anahtar]) => {
              const meta = firmaKatilimMeta(firma);
              return (
                <span key={anahtar} className="d-flex align-items-center gap-2">
                  <LegendSerit hex={statusBackground(meta.color)}>{meta.label}</LegendSerit>
                  <span className="text-muted small">{t(`personelKart.${anahtar}`)}</span>
                </span>
              );
            })}
          </LegendSection>
        )}

        <LegendSection title={t("personelKart.sayacYigini")}>
          {[["warning", "bekleyenBelge"], ["success", "onaylananBelge"], ["danger", "reddedilenBelge"]].map(([tone, key]) => (
            <span key={key} className="d-flex align-items-center gap-2 small">
              <SayacKutusu tone={tone} value={2} label={t(`personelKart.${key}`)} />
              {t(`personelKart.${key}`)}
            </span>
          ))}
          <div className="text-muted small">{t("personelKart.sayacSifirGizli")}</div>
        </LegendSection>

        <LegendSection title={t("personelKart.eslemeDurumuFuarKadrosu")}>
          {ESLESME_DURUM_IDS.map((id) => (
            <span key={id} className="d-flex align-items-center gap-2">
              <EslesmeDurumRozeti durum={id} />
              <span className="text-muted small">{ESLESME_ACIKLAMA[id]}</span>
            </span>
          ))}
        </LegendSection>

        <LegendSection title={t("personelKart.diger")}>
          <span className="d-inline-flex align-items-center gap-2">
            <Badge color="dark" className="d-inline-flex align-items-center gap-1 fw-normal"><UserMinus size={11} />{t("personelKart.iliskiSonlandirildi")}</Badge>
            <span className="text-muted small">{t("personelKart.kisininBuFirmaylaIliskisiSona")}</span>
          </span>
        </LegendSection>
      </ModalBody>
    </Modal>
  );
}

/**
 * Liste başlığına konan "(i)" düğmesi — tıklanınca yukarıdaki legend modalını açar.
 * Hem denetmen hem firma portalı listesi bunu aynı şekilde kullanır.
 */
export function PersonelLegendButton({ className = "", kisiListesi = false, portal = false }) {
  const { t } = useTranslation("components");
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        id="personel-legend-btn" color="light" size="sm"
        className={`border rounded-circle p-0 d-inline-flex align-items-center justify-content-center ${className}`}
        style={{ width: 26, height: 26 }}
        onClick={() => setOpen(true)}
      >
        <Info size={14} />
      </Button>
      <UncontrolledTooltip target="personel-legend-btn" placement="top">
        {t("personelKart.kartIsaretleriniAcikla")}
      </UncontrolledTooltip>
      <PersonelLegendModal isOpen={open} toggle={() => setOpen((o) => !o)} kisiListesi={kisiListesi} portal={portal} />
    </>
  );
}
